import { asList, Decimal, Id, IdOr, ListOr, Maybe, Timestamp } from '../data.js';
import { AttributionSourceType, BuyerStatus, UserPurchaseOrderApplicationStatus } from './user-statuses.js';
import { User } from './users.js';
import { DomainId } from './domains.js';
import { ContentItem } from './content.js';
import { capitalize, lazy, makeIndex, unique } from '../util.js';

import { Entity } from './entities.js';
import { Lead } from './leads.js';
import { Address } from './address.js';
import { PaymentMethod } from './payments.js';
import { CurrencyId } from './countries.js';
import { Upload } from './designs.js';
import { TierCategory } from './stores.js';

export interface PublicAccount {
	
	id: string & { __entity?: 'Account' };
	domain: DomainId;
	
	/**
	 * Precomputed as name ?? business_name ?? 'first_name last_name'
	 */
	account_name: Maybe<string>;
	
	/**
	 * Thumb in data/public/avatars, orig in data/private/avatars
	 */
	avatar: Maybe<string>;
	
	/**
	 * Precomputed as email ?? owner.email. May be omitted (unlike OwnAccount).
	 */
	account_email?: Maybe<string>;
	
}

export type AccountType = 'user' | 'team';

export interface OwnAccount extends PublicAccount {
	
	type: AccountType;
	
	/**
	 * Specific user this account is tied to. Used rather vaguely at the moment, not guaranteed to be present, absent, or unique.
	 */
	user: Maybe<Id<User>>;
	
	/**
	 * Precomputed as email ?? owner.email
	 */
	account_email: string;
	
	/**
	 * Entered manually, null by default
	 */
	name: Maybe<string>;
	/**
	 * Entered manually, null by default
	 */
	email: Maybe<string>;
	
	tier: string;
	tier_locked_until: Maybe<Timestamp>;
	min_tier: Maybe<string>;
	max_tier: Maybe<string>;
	
	is_league: boolean;
	league_alias: Maybe<string>;
	league_secret: Maybe<string>;
	
	is_sales_rep: boolean;
	
	/** @deprecated unused */
	dob: string | null;
	
	business_name: Maybe<string>;
	
	main_address: Maybe<Id<Address>>;
	
	po_application_status: Maybe<UserPurchaseOrderApplicationStatus>;
	po_application_submitted: Maybe<Timestamp>;
	estimated_annual_volume: Maybe<string>;
	
	/** If set, tax-exempt *in all states*. See issue #281 */
	tax_exempt: Maybe<boolean>;
	/** Tax-exempt in listed states only */
	tax_exempt_states: Maybe<string[]>;
	/** Unused in logic, entered in settings */
	tax_state: Maybe<string>;
	tax_exempt_proof: Maybe<Id<Upload>>;
	
	requested_net_term_days: Maybe<string>;
	requested_limit: Maybe<string>;
	
	// TODO obscure when no access?
	auto_payment_method: Maybe<Id<PaymentMethod>>;
	
	net_term_days: Maybe<number>;
	credit_limit: Maybe<Decimal>;
	
	contact_preference: Maybe<AccountContactPreference>;
	discord_id: Maybe<string>;
	org_type: Maybe<AccountOrgType | string>; // FIXME string
	org_structure: Maybe<string>;
	sports: Maybe<string>;
	games: Maybe<string>;
	us_employee_count: Maybe<string>;
	us_location_count: Maybe<string>;
	merch_needs: Maybe<string[]>; // idk
	links: Maybe<string>;
	rostered_players: Maybe<string>;
	
	last_action: Maybe<Timestamp>;
	
}

export const accountOrgTypes = ['business', 'esports', 'sports', 'creator'] as const;
export type AccountOrgType = (typeof accountOrgTypes)[number];

export const accountContactPreferences = ['email', 'phone', 'discord'] as const;
export type AccountContactPreference = (typeof accountContactPreferences)[number];

export interface Account extends OwnAccount {

	notes?: Maybe<string>;
	buyer_status?: Maybe<BuyerStatus>;
	
	stripe_customer?: string;
	
	free_shipping_usa: boolean;
	free_shipping_non_usa: boolean;
	pick_up: boolean;
	allow_store_payment: Maybe<boolean>;
	payment_currency: Maybe<CurrencyId>;
	
	member_tier: Maybe<string>;
	league_welcome: Maybe<ContentItem>;
	league_index: Maybe<ContentItem>;
	
	commission_model_override: Maybe<Record<PayableAccountRelationType, Maybe<Id<CommissionsModel>>>>;
	
	created: Timestamp;
	updated: Timestamp;
	
	/** Virtual, see AccountWithAccess */
	account_access?: Maybe<AccountAccessData>;
	
}

export type UserOrAccount = { user: IdOr<User>; account?: never } | { user?: never; account?: IdOr<Account> };

export interface AccountAttribution {

	id: number;
	account: Maybe<Id<Account>>;
	user: Maybe<Id<User>>;
	
	referrer: Maybe<Id<Account>>;
	lead: Maybe<Id<Lead>>;

	source_type?: Maybe<AttributionSourceType>;
	first_visit?: Maybe<Timestamp>;
	first_visit_url?: Maybe<string>;
	first_visit_referrer?: Maybe<string>;
	first_visit_paid?: Maybe<boolean>;
	
	created: Timestamp;
	updated: Timestamp;
	
}

export interface CommissionsModel {

	id: string & { __entity?: 'CommissionsModel' };
	name: string;
	model: CommissionsModelConfig;
	
	created: Timestamp;
	updated: Timestamp;
	
}
export type CommissionsModelConfig = Record<'default' | Id<TierCategory>, Maybe<number>> & { default: number };

export const accountRelationTypes = [
	
	/** League (etc) that the account is a part of. Similar to sales rep. Gets commissions, visible to the account. */
	'league',
	
	/** Main sales representative for the account. Gets commissions, visible to the account. */
	'sales_rep',
	
	/** Assistant sales rep, sets the initial meetings etc. Gets (smaller) commissions, not visible to the account. */
	'setter',
	
	/** SigmaWear staff. Does not get commissions, visible to the account. */
	'support_rep',
	
	/** Automatically assigned via cookie when visiting an affiliate link or a store. Gets commissions for non-store purchases, not visible to the account. */
	'referrer',
	
] as const;

/** Relation type. See {@link accountRelationTypes} */
export type AccountRelationType = (typeof accountRelationTypes)[number];

/** Subset of {@link AccountRelationType} that may be paid commissions. */
export type PayableAccountRelationType = Exclude<AccountRelationType, 'support_rep'>;
export const payableRelationTypes = ['league', 'sales_rep', 'setter', 'referrer'] as const;

/** Superset of {@link AccountRelationType} for e.g. AdminAccountModel rows where 'owner' is concated as a relation (despite actually being access). */
export type ComputedRelationType = AccountRelationType | 'owner';
export const computedRelationTypes = [...accountRelationTypes, 'owner'] as const;

export interface AccountRelation {

	id: number & { __entity?: 'AccountRelation' };
	
	/**
	 * Account this *applies to*, e.g. member of the league or customer of the sales rep.
	 */
	to_account: Id<Account>;
	/**
	 * Account of the *relation*, e.g. league or sales rep.
	 */
	from_account: Id<Account>;
	
	active: boolean;
	
	type: AccountRelationType;
	priority: number;
	commissions_model: Maybe<Id<CommissionsModel>>;
	
	created: Timestamp;
	updated: Timestamp;
	
	/** Virtual */
	from_account_info?: Maybe<Account>;
	
}

export const specialAccountAccessTypes = ['site_admin', ...accountRelationTypes] as const;
export type SpecialAccountAccess = ('site_admin' | AccountRelationType) & AccountPermission;

/**
 * Account access, may be merged by deriveUserAccountAccess (hence no definite id)
 */
export interface AccountAccessData {
	
	to_account: Id<Account>;
	from_account: Maybe<Id<Account>>;
	from_user: Maybe<Id<User>>;
	special: Maybe<SpecialAccountAccess>;
	expires: Maybe<Timestamp>;
	active: boolean;
	last_action: Maybe<Timestamp>;
	permissions: AccountPermission[];
	
	invited_email: Maybe<string>;
	invited_user: Maybe<Id<User>>;
	
	budget_limit: Maybe<Decimal>;
	budget_since: Maybe<string>;
	budget_reset?: Maybe<string>;
	
	created?: Maybe<Timestamp>;
	updated?: Maybe<Timestamp>;
	
	/* Virtual below */
	
	account_name?: Maybe<string>;
	account_email?: Maybe<string>;
	account_avatar?: Maybe<string>;
	account_type?: Maybe<AccountType>;
	user_email?: Maybe<string>;
	from_name?: Maybe<string>;
	from_email?: Maybe<string>;
	account_is_sales_rep?: Maybe<boolean>;
	account_is_league?: Maybe<boolean>;
	
	via_ids?: Maybe<Id<AccountAccess>[]>;
	via_accounts?: Maybe<Id<Account>[]>;
	
	budget_used?: Maybe<Decimal>;
	budget_remaining?: Maybe<Decimal>;
	
}

export const accountAccessDataFields = [
	'to_account',
	'from_account',
	'from_user',
	'account_name',
	'account_email',
	'account_avatar',
	'account_type',
	'user_email',
	'from_email',
	'permissions',
	'active',
	'last_action',
	'invited_email',
	'invited_user',
	'special',
	'expires',
	'created',
	'updated',
	'via_ids',
	'via_accounts',
	'account_is_sales_rep',
	'account_is_league',
	'budget_limit',
	'budget_since',
	'budget_reset',
	'special',
	'expires',
] as const;

/**
 * Specific account_access row.
 */
export interface AccountAccess extends AccountAccessData {
	
	id: number;
	
	created: Timestamp;
	updated: Timestamp;
	
}
export interface AccountWithAccess extends Entity<'accounts'> {
	account_access: AccountAccessData;
}

export const accountPermissions = [

	'act_as_account',
	
	'owner',
	'manager',
	'admin',
	'designer',
	'order_entry',
	'store_manager',
	'treasurer',
	
	'view_access',
	'edit_access',
	'add_access',
	'edit_access_unrestricted',
	'transfer_ownership',
	'convert_to_team',
	
	'view_account_info',
	'edit_account_info',
	
	'view_addresses',
	'own_addresses',
	'all_addresses',
	'edit_addresses',
	
	'view_store',
	'edit_store_setup',
	'edit_store_draft',
	'publish_store',
	
	'view_carts',
	'own_carts',
	'all_carts',
	'edit_carts',
	
	'view_purchases',
	'own_purchases',
	'all_purchases',
	'full_purchase_info',
	'create_purchase',
	'approve_purchase',
	
	'budget_purchase',
	'store_budget_purchase',
	'any_budget_purchase',
	'auto_approve_budget',
	'assign_budget',
	
	'view_payments',
	'own_payments',
	'all_payments',
	'pay_invoice',
	
	'view_payment_methods',
	'own_payment_methods',
	'use_shared_payment_methods',
	'all_payment_methods',
	'use_payment_methods',
	'edit_payment_methods',
	
	'store_commissions',
	'affiliate_commissions',
	'all_commissions',
	
	'create_payout',
	
	'view_logos',
	'own_logos',
	'all_logos',
	'edit_logos',
	
	'view_designs',
	'own_designs',
	'all_designs',
	'edit_designs',
	
	'view_rosters',
	'own_rosters',
	'all_rosters',
	'edit_rosters',
	
	'join_as_affiliate',
	'view_affiliates',
	'edit_affiliates',
	'access_affiliate_accounts',
	
	'view_promocodes',
	'edit_promocodes',
	
	'site_admin',
	'sales_rep',
	'league',
	
] as const;
export type AccountPermission = (typeof accountPermissions)[number];

export interface AccountPermissionConfig {
	id: AccountPermission;
	group: AccountPermissionGroup;
	label?: string;
	description?: string;
	dangerous?: boolean;
	color?: string;
	implies?: AccountPermission[];
	allImplied: AccountPermission[];
	perStore?: boolean;
	locked?: boolean;
	hidden?: boolean;
}

export const accountPermissionGroups = ['predefined', 'access', 'account_info', 'assets', 'store', 'carts', 'addresses', 'purchases', 'budget', 'payments', 'commissions', 'affiliate'] as const;
export type AccountPermissionGroup = (typeof accountPermissionGroups)[number];

export const accountPermissionGroupInfo: Record<AccountPermissionGroup, {
	name?: string;
	description?: string;
}> = {
	predefined: {
		name: 'Predefined Roles',
		description: 'These do not grant any access by themselves but enable a number of related permissions.',
	},
	access: {},
	account_info: {},
	assets: {},
	store: {},
	carts: {},
	addresses: {},
	purchases: {},
	budget: {},
	payments: {},
	commissions: {},
	affiliate: {
		description: 'Note: this functionality is only made available to specific accounts by SigmaWear staff.',
	},
};

const accountPermissionConfigBase: Record<AccountPermission, Omit<AccountPermissionConfig, 'id' | 'allImplied'>> = {

	act_as_account: {
		label: 'Act as Account',
		description: 'Being able to switch to this account and perform actions (as set by other permissions)',
		implies: ['view_account_info'],
		group: 'access',
	},
	view_access: {
		description: 'See account users, including their email and permissions.',
		implies: ['act_as_account'],
		group: 'access',
	},
	edit_access: {
		description: 'Add or revoke permissions from existing access records, restricted to those that this user has themselves.',
		implies: ['view_access'],
		group: 'access',
	},
	add_access: {
		description: 'Add new users and grant permissions, restricted to those that this user has themselves.',
		implies: ['edit_access'],
		group: 'access',
	},
	edit_access_unrestricted: {
		description: 'Add or remove any permissions, except for ownership transfer.',
		implies: ['edit_access'],
		group: 'access',
		dangerous: true,
		hidden: true,
	},
	convert_to_team: {
		description: 'Convert user account account to a team',
		group: 'access',
		hidden: true,
	},
	transfer_ownership: {
		description: 'Transfer account ownership',
		implies: ['edit_access_unrestricted'],
		group: 'access',
		dangerous: true,
		hidden: true,
	},
	
	view_account_info: {
		implies: [],
		group: 'account_info',
	},
	edit_account_info: {
		implies: ['act_as_account'],
		group: 'account_info',
	},
	
	view_store: {
		description: 'Read access to the store(s)',
		perStore: true,
		implies: ['act_as_account'],
		group: 'store',
	},
	edit_store_setup: {
		description: 'Can edit store information, pricing, logo, banners, etc.',
		perStore: true,
		implies: ['view_store'],
		group: 'store',
	},
	edit_store_draft: {
		description: 'Product management - add products to draft, descriptions, collections.',
		perStore: true,
		implies: ['view_store'],
		group: 'store',
	},
	publish_store: {
		description: 'Publish the store draft',
		perStore: true,
		implies: ['edit_store_setup', 'edit_store_draft'],
		group: 'store',
	},
	view_promocodes: {
		description: 'See promocodes and links.',
		implies: ['act_as_account', 'view_store'],
		perStore: true,
		group: 'store',
	},
	edit_promocodes: {
		description: 'Add and remove promocodes.',
		implies: ['view_promocodes'],
		perStore: true,
		group: 'store',
	},
	
	view_carts: {
		description: 'Show the list (if granted the all/own permissions).',
		hidden: true,
		group: 'carts',
	},
	own_carts: {
		description: 'Create, edit and delete own carts.',
		implies: ['view_carts'],
		group: 'carts',
	},
	all_carts: {
		description: 'View all open carts.',
		implies: ['view_carts'],
		group: 'carts',
	},
	edit_carts: {
		description: 'Edit and remove all open carts',
		implies: ['own_carts', 'all_carts'],
		group: 'carts',
	},
	
	view_purchases: {
		description: 'Show the list (if granted the all/own permissions).',
		hidden: true,
		group: 'purchases',
	},
	own_purchases: {
		description: 'See purchases made by this user but billed to the account',
		implies: ['view_purchases'],
		group: 'purchases',
	},
	all_purchases: {
		description: 'See all purchases placed via or billed to the account (except sensitive info)',
		implies: ['view_purchases'],
		group: 'purchases',
	},
	create_purchase: {
		description: 'Place draft orders that need to be approved and paid by other users.',
		implies: ['own_purchases'],
		group: 'purchases',
	},
	approve_purchase: {
		description: 'Approve previously created orders and place new orders without approval.',
		implies: ['create_purchase', 'view_purchases'],
		group: 'purchases',
	},
	
	budget_purchase: {
		description: 'Create orders using the assigned budget. Requires more specific budget permissions.',
		implies: ['own_purchases', 'act_as_account', 'own_carts'],
		group: 'budget',
	},
	store_budget_purchase: {
		description: `Place budget orders for products in this account's stores.`,
		implies: ['budget_purchase'],
		group: 'budget',
	},
	any_budget_purchase: {
		description: 'Place budget orders for any products (store, designer, services, etc.)',
		implies: ['store_budget_purchase'],
		group: 'budget',
	},
	auto_approve_budget: {
		label: 'Auto-approve budget',
		description: 'Budget orders immediately paid with the account default payment method, if possible.',
		implies: ['store_budget_purchase'],
		group: 'budget',
	},
	assign_budget: {
		description: 'Enable budget purchase and set limits for all users.',
		implies: ['edit_access', 'all_purchases', 'all_payment_methods'],
		group: 'payments',
	},
	
	view_addresses: {
		description: 'Show the list (if granted the all/own permissions).',
		implies: [],
		hidden: true,
		group: 'addresses',
	},
	own_addresses: {
		description: 'Add (including through checkout), list and remove own saved addresses.',
		implies: ['view_addresses'],
		group: 'addresses',
	},
	all_addresses: {
		description: 'See all saved addresses and use them as shipping/billing address.',
		implies: ['act_as_account'],
		group: 'addresses',
	},
	edit_addresses: {
		description: 'Add, edit and remove all saved addresses.',
		implies: ['all_addresses', 'own_addresses'],
		group: 'addresses',
	},
	
	full_purchase_info: {
		description: 'See names, addresses, invoices, payment status, payment method, etc. on all purchases.',
		implies: ['all_purchases', 'all_addresses', 'all_payments'],
		group: 'purchases',
	},
	
	view_payments: {
		description: 'Show the list (if granted the all/own permissions).',
		hidden: true,
		group: 'payments',
	},
	own_payments: {
		description: 'See an overview of previous payments or outstanding invoices on orders placed by user, as well as payments made by this user.',
		implies: ['act_as_account', 'view_payments'],
		group: 'payments',
	},
	all_payments: {
		description: 'See an overview of all previous payments and outstanding invoices.',
		implies: ['view_payments'],
		group: 'payments',
	},
	pay_invoice: {
		description: 'Pay for a previously created and approved order with an allowed payment method.',
		implies: ['view_purchases', 'own_payments'],
		group: 'payments',
	},
	
	view_payment_methods: {
		description: 'Show the list (if granted the all/own permissions).',
		group: 'payments',
		hidden: true,
	},
	own_payment_methods: {
		description: 'Save own payment methods, edit and use them, or pay by entering payment method directly.',
		implies: ['view_payment_methods'],
		group: 'payments',
	},
	all_payment_methods: {
		description: 'See a list of existing saved payment methods.',
		implies: ['view_payment_methods'],
		group: 'payments',
	},
	use_shared_payment_methods: {
		description: 'Pay using payment methods explicitly marked as shared.',
		implies: ['view_payment_methods'],
		group: 'payments',
	},
	use_payment_methods: {
		description: 'Use all saved payment methods to pay for a purchase.',
		implies: ['all_payment_methods', 'use_shared_payment_methods'],
		group: 'payments',
	},
	edit_payment_methods: {
		description: 'Add and remove payment methods, mark them as shared, enable auto-payment of approved purchases with selected method',
		implies: ['all_payment_methods', 'use_payment_methods', 'own_payment_methods', 'use_shared_payment_methods'],
		group: 'payments',
	},
	
	store_commissions: {
		description: 'See commissions from store orders.',
		implies: ['act_as_account'],
		perStore: true,
		group: 'commissions',
	},
	affiliate_commissions: {
		description: 'See commissions from affiliates.',
		implies: ['act_as_account'],
		perStore: true,
		group: 'commissions',
	},
	all_commissions: {
		description: 'See full commissions history, incl. payouts etc',
		implies: ['act_as_account', 'store_commissions', 'affiliate_commissions'],
		group: 'commissions',
	},
	create_payout: {
		description: 'Pay with commissions, create gift card, request payout',
		implies: ['all_commissions'],
		group: 'commissions',
		dangerous: true,
	},
	
	view_logos: {
		description: 'Show the list (if granted the all/own permissions).',
		hidden: true,
		group: 'assets',
	},
	own_logos: {
		description: 'Upload new logos, list and delete them.',
		implies: ['view_logos'],
		group: 'assets',
	},
	all_logos: {
		description: 'See and use all uploaded logos.',
		implies: ['view_logos'],
		group: 'assets',
	},
	edit_logos: {
		description: 'Delete any uploaded logos.',
		implies: ['all_logos', 'own_logos'],
		group: 'assets',
	},
	
	view_designs: {
		description: 'Show the list (if granted the all/own permissions).',
		hidden: true,
		group: 'assets',
	},
	own_designs: {
		description: 'Create new designs and save them to the account, list and delete them.',
		implies: ['view_designs'],
		group: 'assets',
	},
	all_designs: {
		description: 'See and use all saved designs, including creating copies.',
		implies: ['view_designs'],
		group: 'assets',
	},
	edit_designs: {
		description: 'Delete any saved designs.',
		implies: ['all_designs', 'own_designs'],
		group: 'assets',
	},
	
	view_rosters: {
		description: 'Show the list (if granted the all/own permissions).',
		hidden: true,
		group: 'assets',
	},
	own_rosters: {
		description: 'Create new saved rosters.',
		implies: ['view_rosters'],
		group: 'assets',
	},
	all_rosters: {
		description: 'See all saved rosters and use them in carts.',
		implies: ['view_rosters'],
		group: 'assets',
	},
	edit_rosters: {
		description: 'Edit and delete existing rosters',
		implies: ['all_rosters', 'own_rosters'],
		group: 'assets',
	},
	
	view_affiliates: {
		description: 'See all affiliate accounts.',
		implies: ['act_as_account'],
		group: 'affiliate',
	},
	edit_affiliates: {
		description: 'Add and remove affiliates',
		implies: ['view_affiliates'],
		group: 'affiliate',
	},
	join_as_affiliate: {
		description: 'Join as an affiliate of another account.',
		implies: ['act_as_account'],
		group: 'affiliate',
	},
	access_affiliate_accounts: {
		description: 'Gain limited access to affiliate account to assist with the setup.',
		implies: ['view_affiliates'],
		group: 'affiliate',
		dangerous: true,
	},
	
	owner: {
		label: 'Owner',
		description: 'Account owner with full access. An account can only have exactly one owner.',
		group: 'predefined',
		implies: ['admin', 'transfer_ownership'],
		color: '#cc0',
	},
	manager: {
		label: 'Manager',
		description: 'Account manager. Full access, except for payments and payouts.',
		color: '#cc9c33',
		group: 'predefined',
		implies: [
			'act_as_account',
			'view_access',
			'add_access',
			'edit_account_info',
			'view_affiliates',
			'edit_affiliates',
			'order_entry',
			'store_manager',
			'designer',
			'edit_carts',
			'edit_rosters',
			'edit_addresses',
			'own_purchases',
			'all_purchases',
			'full_purchase_info',
			'all_addresses',
			'create_purchase',
			'approve_purchase',
			'all_payments',
			'pay_invoice',
			'all_payment_methods',
			'own_payment_methods',
			'use_shared_payment_methods',
			'all_commissions',
		],
	},
	admin: {
		label: 'Administrator',
		description: 'Complete access. Cannot transfer ownership.',
		color: '#c63',
		group: 'predefined',
		implies: [
			'manager',
			'join_as_affiliate',
			'access_affiliate_accounts',
			'edit_access_unrestricted',
			'edit_payment_methods',
			'create_payout',
			'treasurer',
			'convert_to_team',
		],
	},
	
	designer: {
		group: 'predefined',
		implies: [
			'act_as_account',
			'edit_logos',
			'edit_designs',
		],
	},
	
	order_entry: {
		label: 'Order Entry',
		group: 'predefined',
		implies: [
			'act_as_account',
			'create_purchase',
			'own_carts',
			'own_logos',
			'own_designs',
			'own_rosters',
			'all_logos',
			'all_designs',
			'all_rosters',
			'own_addresses',
			'own_payments',
			'own_purchases',
		],
	},
	
	store_manager: {
		label: 'Store Manager',
		group: 'predefined',
		implies: [
			'act_as_account',
			'view_store',
			'edit_store_setup',
			'edit_store_draft',
			'publish_store',
			'view_promocodes',
			'edit_promocodes',
			'all_designs',
		],
	},
	
	treasurer: {
		label: 'Treasurer',
		group: 'predefined',
		implies: [
			
			'act_as_account',
			'all_purchases',
			'full_purchase_info',
			'all_addresses',
			'approve_purchase',
			
			'all_payments',
			'pay_invoice',
			'edit_payment_methods',
			
			'assign_budget',
			
		],
	},
	
	site_admin: {
		label: 'Support Staff',
		description: 'Temporary access by SigmaWear staff',
		implies: ['admin', 'transfer_ownership'],
		locked: true,
		group: 'predefined',
		hidden: true,
		color: '#0cc',
	},
	sales_rep: {
		label: 'Sales Representative',
		description: 'Temporary access by your sales representative',
		implies: ['manager', 'convert_to_team'],
		locked: true,
		group: 'predefined',
		hidden: true,
		color: '#c8c',
	},
	league: {
		label: 'League Representative',
		description: 'Temporary access by your league representative',
		implies: ['manager', 'convert_to_team'],
		locked: true,
		group: 'predefined',
		hidden: true,
		color: '#c8c',
	},
	
};

function computeImplied(perms: AccountPermission[] = []): AccountPermission[] {
	return [...perms, ...perms.flatMap(p => computeImplied(accountPermissionConfigBase[p].implies))];
}

export const accountPermissionList = lazy(() => Object.entries(accountPermissionConfigBase).map(([id, cfg]) => ({
	id,
	label: capitalize(id.replace(/_/g, ' ')),
	...cfg,
	color: cfg.color ?? (cfg.dangerous ? '#f77' : '#ccc'),
	allImplied: unique(computeImplied(cfg.implies)),
} as AccountPermissionConfig)).toSorted((a, b) => (accountPermissionGroups.indexOf(a.group) - accountPermissionGroups.indexOf(b.group)) || (accountPermissions.indexOf(a.id) - accountPermissions.indexOf(b.id))));
export const accountPermissionMap = lazy(() => makeIndex(accountPermissionList()));

export function permissionImplies(perm: AccountPermission, need: AccountPermission): boolean {
	return perm == need || accountPermissionMap()[perm].implies?.some(p => permissionImplies(p, need)) || false;
}

/**
 * Check whether `have` includes (directly or implied) all (default) or some (some = true) of the permission(s) in `need`.
 * @param have Existing permissions.
 * @param need Required permissions.
 * @param some Whether any one of listed permissions will suffice.
 */
export function permissionsInclude(have: AccountPermission[], need: ListOr<AccountPermission>, some = false): boolean {
	need = asList(need);
	const check = (np: AccountPermission) => have.some(pp => permissionImplies(pp, np));
	return some ? need.some(check) : need.every(check);
}

/**
 * Helper for {@link permissionsInclude}. Returns false if aa is {@link None}.
 * @param aa
 * @param permission
 * @param some
 */
export function accountAccessIncludes(aa: Maybe<{ permissions?: AccountPermission[] }>, permission: ListOr<AccountPermission>, some = false): boolean {
	return aa?.permissions && permissionsInclude(aa.permissions, permission, some) || false;
}

/**
 * Returns only entries in list which have all of these permissions.
 * Always also removes entries with no permissions at all (possible if it's A->B->C and A->B and B->C do not intersect)
 * @param list
 * @param permissions
 */
export function filterAccountAccess(list: AccountAccessData[], permissions: AccountPermission[] = []) {
	return list.filter(aa => !!aa.permissions.length && permissions.every(p => accountAccessIncludes(aa, p)));
}

/**
 * Curried version of filterAccountAccess to pass to then().
 * @param permissions
 */
export const accountAccessFilter = (permissions: AccountPermission[] = []) => (list: AccountAccessData[]) => filterAccountAccess(list, permissions);

